import { useEffect, useState } from "react";
import { Plus, LogIn, LogOut, UtensilsCrossed, Pill, Dog, NotebookPen, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr, fmtDate, todayStr } from "../../lib/api";
import ReceiptModal from "../../components/ReceiptModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";

const ACTIVITY_TYPES = [
  { id: "feeding", label: "Feeding", icon: UtensilsCrossed },
  { id: "medication", label: "Medication", icon: Pill },
  { id: "walk", label: "Walk", icon: Dog },
  { id: "note", label: "Note", icon: NotebookPen },
];

export default function Boarding() {
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [occ, setOcc] = useState(null);
  const [resDialog, setResDialog] = useState(false);
  const [form, setForm] = useState({ customer_id: "", pet_id: "", room_id: "", check_in: todayStr(), check_out: "", notes: "" });
  const [pets, setPets] = useState([]);
  const [detail, setDetail] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actForm, setActForm] = useState({ type: "feeding", notes: "" });
  const [checkinDialog, setCheckinDialog] = useState(false);
  const [checkinForm, setCheckinForm] = useState({ deposit: "", belongings: "" });
  const [checkoutDialog, setCheckoutDialog] = useState(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [receiptId, setReceiptId] = useState(null);

  const load = () => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/boarding/reservations").then((r) => setReservations(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/boarding/occupancy").then((r) => setOcc(r)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/boarding/occupancy").then((r) => setOcc(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setPets([]);
    if (form.customer_id) api.get(`/customers/${form.customer_id}`).then((r) => setPets(r.data.pets)).catch(() => {});
  }, [form.customer_id]);

  const activeForRoom = (roomId) => {
    const today = todayStr();
    return reservations.find((r) => r.room_id === roomId && ["reserved", "checked_in"].includes(r.status) && r.check_in <= today && r.check_out > today);
  };

  const openDetail = async (r) => {
    setDetail(r);
    const { data } = await api.get(`/boarding/reservations/${r.id}/activities`);
    setActivities(data);
  };

  const createReservation = async () => {
    try {
      await api.post("/boarding/reservations", form);
      toast.success("Reservation created");
      setResDialog(false);
      setForm({ customer_id: "", pet_id: "", room_id: "", check_in: todayStr(), check_out: "", notes: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doCheckin = async () => {
    try {
      await api.post(`/boarding/reservations/${detail.id}/checkin`, { deposit: parseFloat(checkinForm.deposit) || 0, belongings: checkinForm.belongings });
      toast.success("Checked in");
      setCheckinDialog(false); setDetail(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doCheckout = async () => {
    try {
      const { data } = await api.post(`/boarding/reservations/${detail.id}/checkout`, {
        payments: [{ method: payMethod, amount: Math.max(0, detail.estimated_total - (detail.deposit || 0)) }],
      });
      toast.success(`Checked out — ${data.sale.receipt_no}`);
      setReceiptId(data.sale.id);
      setCheckoutDialog(false); setDetail(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const cancel = async (id) => {
    try { await api.post(`/boarding/reservations/${id}/cancel`); toast.success("Reservation cancelled"); setDetail(null); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const addActivity = async () => {
    try {
      await api.post(`/boarding/reservations/${detail.id}/activities`, actForm);
      toast.success("Activity logged");
      setActForm({ type: "feeding", notes: "" });
      openDetail(detail);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const due = detail ? Math.max(0, detail.estimated_total - (detail.deposit || 0)) : 0;

  return (
    <div className="animate-rise space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="boarding-title">Boarding &amp; Daycare</h1>
          <p className="text-sm text-slate-500">
            Rooms grid · {occ ? `${occ.occupancy_rate}% occupancy (7-day)` : "loading occupancy…"}
          </p>
        </div>
        <Button className="bg-[#2A9D8F] hover:bg-[#238b7e]" onClick={() => setResDialog(true)} data-testid="new-reservation-btn">
          <Plus className="w-4 h-4 mr-2" /> New Reservation
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {rooms.map((room) => {
          const active = activeForRoom(room.id);
          const status = !active ? "vacant" : active.status === "checked_in" ? "occupied" : "reserved";
          const styles = {
            vacant: "border-[#BFE8E9] bg-white",
            occupied: "border-[#2A9D8F] bg-teal-50",
            reserved: "border-[#F4A261] bg-amber-50",
          };
          return (
            <button key={room.id} onClick={() => active && openDetail(active)} data-testid={`room-card-${room.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-2xl border-2 p-4 text-left transition-transform hover:-translate-y-0.5 ${styles[status]}`}>
              <div className="flex items-center justify-between">
                <p className="font-display font-semibold text-sm">{room.name}</p>
                <Badge className={`text-[9px] capitalize ${status === "occupied" ? "bg-[#2A9D8F] hover:bg-[#2A9D8F]" : status === "reserved" ? "bg-[#F4A261] hover:bg-[#F4A261] text-[#093F46]" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`}>
                  {status}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">{peso(room.daily_rate)}/night</p>
              {active ? (
                <p className="text-sm font-semibold mt-2 text-[#0E7C84]">{active.pet_name} <span className="font-normal text-slate-400">· {active.customer_name}</span></p>
              ) : (
                <p className="text-xs text-slate-400 mt-2">Ready for booking</p>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
        <h3 className="font-display font-semibold mb-3">All reservations</h3>
        <div className="space-y-2">
          {reservations.map((r) => (
            <button key={r.id} className="w-full text-left flex flex-wrap items-center justify-between gap-3 border border-[#E2F5F5] rounded-xl p-3 hover:border-[#2A9D8F] transition-colors" onClick={() => openDetail(r)} data-testid={`reservation-row-${r.id.slice(0, 8)}`}>
              <div>
                <p className="font-semibold text-sm">{r.pet_name} — {r.room_name}</p>
                <p className="text-xs text-slate-500">{r.customer_name} · {fmtDate(r.check_in)} → {fmtDate(r.check_out)} · {r.nights} night/s {r.source === "online" && "· online"}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display font-bold text-[#2A9D8F]">{peso(r.estimated_total)}</span>
                <Badge variant="outline" className="capitalize text-[10px]">{r.status.replace("_", " ")}</Badge>
              </div>
            </button>
          ))}
          {reservations.length === 0 && <p className="text-sm text-slate-400">No reservations yet.</p>}
        </div>
      </div>

      <Dialog open={resDialog} onOpenChange={setResDialog}>
        <DialogContent data-testid="reservation-dialog">
          <DialogHeader><DialogTitle className="font-display">New reservation</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, pet_id: "" })}>
                <SelectTrigger data-testid="res-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pet</Label>
              <Select value={form.pet_id} onValueChange={(v) => setForm({ ...form, pet_id: v })} disabled={!form.customer_id}>
                <SelectTrigger data-testid="res-pet"><SelectValue placeholder="Select pet" /></SelectTrigger>
                <SelectContent>{pets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Room</Label>
              <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                <SelectTrigger data-testid="res-room"><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} — {peso(r.daily_rate)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Check-in</Label><Input type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="res-checkin" /></div>
            <div><Label>Check-out</Label><Input type="date" min={form.check_in} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="res-checkout" /></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="res-notes" /></div>
          </div>
          <Button className="bg-[#2A9D8F] hover:bg-[#238b7e] mt-2" onClick={createReservation}
            disabled={!form.customer_id || !form.pet_id || !form.room_id || !form.check_out} data-testid="res-save-btn">
            Create reservation
          </Button>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="reservation-drawer">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display">{detail.pet_name} — {detail.room_name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="bg-[#E2F5F5] rounded-xl p-4 text-sm space-y-1">
                  <p><span className="text-slate-500">Owner:</span> {detail.customer_name}</p>
                  <p><span className="text-slate-500">Stay:</span> {fmtDate(detail.check_in)} → {fmtDate(detail.check_out)} ({detail.nights} night/s)</p>
                  <p><span className="text-slate-500">Rate:</span> {peso(detail.daily_rate)}/night · <span className="text-slate-500">Est. total:</span> <strong>{peso(detail.estimated_total)}</strong></p>
                  <p><span className="text-slate-500">Deposit:</span> {peso(detail.deposit || 0)}</p>
                  {detail.belongings && <p><span className="text-slate-500">Belongings:</span> {detail.belongings}</p>}
                  {detail.notes && <p><span className="text-slate-500">Notes:</span> {detail.notes}</p>}
                </div>

                <div className="flex flex-wrap gap-2">
                  {detail.status === "reserved" && (
                    <>
                      <Button className="bg-[#2A9D8F] hover:bg-[#238b7e]" size="sm" onClick={() => setCheckinDialog(true)} data-testid="checkin-btn">
                        <LogIn className="w-4 h-4 mr-1" /> Check in
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-500" onClick={() => cancel(detail.id)} data-testid="cancel-res-btn">
                        <Ban className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                    </>
                  )}
                  {detail.status === "checked_in" && (
                    <Button className="bg-[#0E7C84] hover:bg-[#093F46]" size="sm" onClick={() => setCheckoutDialog(true)} data-testid="checkout-btn">
                      <LogOut className="w-4 h-4 mr-1" /> Check out &amp; bill
                    </Button>
                  )}
                </div>

                {detail.status === "checked_in" && (
                  <div>
                    <h4 className="font-display font-semibold mb-2">Log activity</h4>
                    <div className="grid grid-cols-4 gap-1.5 mb-2">
                      {ACTIVITY_TYPES.map((t) => (
                        <button key={t.id} onClick={() => setActForm({ ...actForm, type: t.id })} data-testid={`activity-${t.id}`}
                          className={`flex flex-col items-center gap-1 border rounded-xl py-2 text-[11px] font-medium transition-colors ${actForm.type === t.id ? "border-[#2A9D8F] bg-teal-50 text-[#2A9D8F]" : ""}`}>
                          <t.icon className="w-4 h-4" />{t.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="Details (e.g. ate all kibble, 5ml meds)" value={actForm.notes} onChange={(e) => setActForm({ ...actForm, notes: e.target.value })} data-testid="activity-notes" />
                      <Button onClick={addActivity} className="bg-[#2A9D8F] hover:bg-[#238b7e]" data-testid="activity-save-btn">Log</Button>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-display font-semibold mb-2">Daily care log</h4>
                  <div className="space-y-1.5">
                    {activities.map((a) => (
                      <div key={a.id} className="flex items-start gap-2 text-sm border-b border-[#E2F5F5] pb-1.5" data-testid={`activity-row-${a.id.slice(0, 8)}`}>
                        <Badge variant="secondary" className="capitalize text-[10px] shrink-0">{a.type}</Badge>
                        <div className="flex-1">
                          <p>{a.notes || "Done"}</p>
                          <p className="text-[10px] text-slate-400">{a.time?.slice(5, 16).replace("T", " ")} · {a.staff_name}</p>
                        </div>
                      </div>
                    ))}
                    {activities.length === 0 && <p className="text-sm text-slate-400">No activities logged yet.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={checkinDialog} onOpenChange={setCheckinDialog}>
        <DialogContent data-testid="checkin-dialog">
          <DialogHeader><DialogTitle className="font-display">Check in — {detail?.pet_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Deposit collected (₱)</Label><Input type="number" value={checkinForm.deposit} onChange={(e) => setCheckinForm({ ...checkinForm, deposit: e.target.value })} data-testid="checkin-deposit" /></div>
            <div><Label>Pet belongings inventory</Label><Input value={checkinForm.belongings} onChange={(e) => setCheckinForm({ ...checkinForm, belongings: e.target.value })} placeholder="e.g. 1 leash, 2kg food, blanket" data-testid="checkin-belongings" /></div>
            <Button className="w-full bg-[#2A9D8F] hover:bg-[#238b7e]" onClick={doCheckin} data-testid="checkin-confirm-btn">Confirm check-in</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={checkoutDialog} onOpenChange={setCheckoutDialog}>
        <DialogContent data-testid="checkout-bill-dialog">
          <DialogHeader><DialogTitle className="font-display">Check out &amp; bill — {detail?.pet_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="bg-[#E2F5F5] rounded-xl p-4 text-sm space-y-1 font-mono">
                <div className="flex justify-between"><span>{detail.nights} night/s × {peso(detail.daily_rate)}</span><span>{peso(detail.estimated_total)}</span></div>
                <div className="flex justify-between text-slate-500"><span>Less: deposit</span><span>-{peso(detail.deposit || 0)}</span></div>
                <div className="flex justify-between font-bold text-base border-t border-[#BFE8E9] pt-1"><span>Balance due</span><span data-testid="checkout-balance">{peso(due)}</span></div>
              </div>
              <RadioGroup value={payMethod} onValueChange={setPayMethod} className="grid grid-cols-3 gap-2">
                {[["cash", "Cash"], ["gcash", "GCash"], ["card", "Card"]].map(([v, l]) => (
                  <label key={v} className={`flex items-center justify-center gap-2 border rounded-xl py-2.5 text-sm font-medium cursor-pointer ${payMethod === v ? "border-[#0E7C84] bg-[#D9F7F8]" : ""}`} data-testid={`checkout-pay-${v}`}>
                    <RadioGroupItem value={v} className="sr-only" />{l}
                  </label>
                ))}
              </RadioGroup>
              <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" onClick={doCheckout} data-testid="checkout-confirm-btn">
                Collect {peso(due)} &amp; check out
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReceiptModal saleId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} allowEmail />
    </div>
  );
}
